import {
  Button,
  DefaultNumberInputField,
  DefaultSpinner,
  FixedScreenHeaderStack,
  Heading,
  InputField,
  SingleCheckboxSelect,
  Text,
  useToast,
} from '../../common/components/defaultUIComponents';
import {
  Box,
  Flex,
  Grid,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
} from '@chakra-ui/react';
import { CompanySettingsRequest } from '~/common/entities/CompanySettings/CompanySettingsRequests';
import { LogoViewer } from '../../common/components/defaultUIComponents/Pictures/LogoViewer';
import CompanySettingsApi from '../../common/api/zeiterfassungServerApi/CompanySettingsApi';
import { CompanySettings } from '~/common/entities/CompanySettings/CompanySettingsTypes';
import { unstable_usePrompt as usePromt } from 'react-router-dom';
import useTranslate from '../../common/utils/translate';
import { Form, Formik } from 'formik';
import {} from '@chakra-ui/react';
const Settings = () => {
  const [{ data: companySettings, isLoading }] =
    CompanySettingsApi.hooks.useCompanySettings();
  const translate = useTranslate();
  const toast = useToast({});

  const { mutateAsync: updateSettings } =
    CompanySettingsApi.hooks.useUpdateCompanySettings();

  const editCompanySettings = (values: CompanySettingsRequest) => {
    const showToastOnSuccess = () => {
      toast({
        title: translate('companySettings.success.toast.title'),
        description: translate('companySettings.success.toast.description'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    };
    updateSettings(values)
      .then(showToastOnSuccess)
      .catch(() => toast({ title: 'generic.error' }));
  };

  function formatMainBreakSliderValue(minute: number) {
    const hours = Math.floor(minute / 60);
    const minutes = minute % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }
  return (
    <Box>
      {!isLoading ? (
        <Formik
          initialValues={companySettings as CompanySettings}
          onSubmit={editCompanySettings}
          enableReinitialize
        >
          {({ dirty, setFieldValue, values }) => {
            usePromt({
              when: dirty,
              message: translate('companySettings.dirty') as string,
            });
            return (
              <Form style={{ height: '100%' }}>
                <FixedScreenHeaderStack>
                  <Flex justify="space-between">
                    <Heading>{translate('companySettings.heading')}</Heading>
                    <Flex gap={2}>
                      <Button
                        as="a"
                        {...{
                          href: 'https://its4-plan.de/company/forgot_password',
                        }}
                      >
                        {translate('authenticate.change.password')}
                      </Button>
                      <Button type="submit">
                        {translate('companySettings.edit.label')}
                      </Button>
                    </Flex>
                  </Flex>
                </FixedScreenHeaderStack>

                <Flex direction="column" h="100%">
                  <Box
                    width="100%"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <Box
                      mt={8}
                      mb={7}
                      borderBottom="1px solid black"
                      width="80%"
                    />
                    <LogoViewer />
                    <Box
                      mt={2}
                      mb={4}
                      borderBottom="1px solid black"
                      width="80%"
                    />
                  </Box>
                  <div style={{ width: '90%', margin: '0 auto' }}>
                    <Grid
                      templateColumns="repeat(4, 1fr)"
                      gap={8}
                      justifyContent="center"
                      alignContent="center"
                      justifyItems="stretch"
                      alignItems="start"
                      mt={6}
                    >
                      {/* first row*/}
                      <Text>{translate('companySettings.company_name')}</Text>
                      <Flex flexDirection="column" gridColumn="span 2">
                        <InputField
                          name="company_name"
                          label=""
                          placeholder=""
                        />
                      </Flex>
                      <Text></Text>
                      {/* second row*/}
                      <Text>
                        {translate('companySettings.autoLogout.time')}
                      </Text>
                      <DefaultNumberInputField
                        name="logout_after_time_over_working_hours"
                        label=""
                        min={2}
                      />{' '}
                      <Text>
                        {translate('companySettings.maxRunningAmount')}
                      </Text>
                      <DefaultNumberInputField
                        name="max_running_amount"
                        label=""
                        min={1}
                      />
                      {/* third row*/}
                      <Text>{translate('companySettings.logoutOnLeave')}</Text>
                      <SingleCheckboxSelect
                        name="logout_on_leave"
                        label=""
                        row
                      />
                      <Text>{translate('order_kw_instead_of_date')}</Text>
                      <SingleCheckboxSelect
                        name="order_kw_instead_of_date"
                        label=""
                        row
                      />
                      {/* fourth row*/}
                      <Text>
                        {translate('companySettings.autoEndTimestamps')}
                      </Text>
                      <SingleCheckboxSelect name="auto_logout" label="" row />
                      <Text>
                        {translate('companySettings.allowLeaveWhenJobsOpen')}
                      </Text>
                      <SingleCheckboxSelect
                        name="allow_leave_when_jobs_open"
                        label=""
                        row
                      />
                      {/* fifth row*/}
                      <Flex flexDirection="column" gridColumn="span 2">
                        <Text mb={2}>
                          {translate('companySettings.mainBreakTime') +
                            ': ' +
                            formatMainBreakSliderValue(
                              values.mainBreakTime ?? 720,
                            )}
                        </Text>
                        <Slider
                          min={0}
                          max={1439}
                          step={5}
                          defaultValue={values.mainBreakTime ?? 720}
                          value={values.mainBreakTime}
                          onChange={(value) =>
                            setFieldValue('mainBreakTime', value)
                          }
                        >
                          <SliderMark
                            value={360}
                            mt="3"
                            ml="-2.5"
                            fontSize="sm"
                          >
                            06:00
                          </SliderMark>
                          <SliderMark
                            value={720}
                            mt="3"
                            ml="-2.5"
                            fontSize="sm"
                          >
                            12:00
                          </SliderMark>
                          <SliderMark
                            value={1080}
                            mt="3"
                            ml="-2.5"
                            fontSize="sm"
                          >
                            18:00
                          </SliderMark>
                          <SliderTrack>
                            <SliderFilledTrack />
                          </SliderTrack>
                          <SliderThumb />
                        </Slider>
                      </Flex>
                      <Flex flexDirection="column" gridColumn="span 2">
                        <Text mb={2}>
                          {translate('companySettings.mainBreakAmount') +
                            ': ' +
                            (values.mainBreakAmount ?? 15) +
                            ' ' +
                            translate('companySettings.minutes')}
                        </Text>
                        <Slider
                          min={15}
                          max={90}
                          step={1}
                          defaultValue={values.mainBreakAmount ?? 15}
                          value={values.mainBreakAmount}
                          onChange={(value) =>
                            setFieldValue('mainBreakAmount', value)
                          }
                        >
                          <SliderMark value={15} mt="3" ml="-2.5" fontSize="sm">
                            15 min
                          </SliderMark>
                          <SliderMark value={90} mt="3" ml="-2.5" fontSize="sm">
                            90 min
                          </SliderMark>
                          <SliderTrack>
                            <SliderFilledTrack />
                          </SliderTrack>
                          <SliderThumb />
                        </Slider>
                      </Flex>
                    </Grid>
                  </div>
                  <Flex justifyContent="center" mt={10}>
                    <Button type="submit">
                      {translate('companySettings.edit.label')}
                    </Button>
                  </Flex>
                </Flex>
              </Form>
            );
          }}
        </Formik>
      ) : (
        <DefaultSpinner />
      )}
    </Box>
  );
};

export default Settings;
